// client/src/lib/queryCache.js

/**
 * Lightweight in-memory + sessionStorage cache with TTL support.
 * Zero external dependencies.
 */
class QueryCache {
  constructor() {
    this.memoryCache = new Map();
    this.storagePrefix = "rm_cache_";
  }

  /**
   * Set cache entry
   * @param {string} key
   * @param {any} data
   * @param {number} ttlMs - Time to live in milliseconds (default 5 mins)
   */
  set(key, data, ttlMs = 5 * 60 * 1000) {
    if (!key) return;
    const expiresAt = Date.now() + ttlMs;
    const entry = { data, expiresAt };

    // 1. In-memory
    this.memoryCache.set(key, entry);

    // 2. SessionStorage fallback (survives component remounts & route transitions)
    try {
      if (typeof window !== "undefined" && window.sessionStorage) {
        window.sessionStorage.setItem(
          `${this.storagePrefix}${key}`,
          JSON.stringify(entry)
        );
      }
    } catch {
      // Storage quota or serialization issue; memory cache still works
    }
  }

  /**
   * Get cached data if present and not expired
   * @param {string} key
   * @returns {any|null}
   */
  get(key) {
    if (!key) return null;
    const now = Date.now();

    // 1. Check in-memory map
    const memEntry = this.memoryCache.get(key);
    if (memEntry) {
      if (memEntry.expiresAt > now) {
        return memEntry.data;
      }
      // Expired in memory
      this.memoryCache.delete(key);
    }

    // 2. Check sessionStorage
    try {
      if (typeof window !== "undefined" && window.sessionStorage) {
        const raw = window.sessionStorage.getItem(`${this.storagePrefix}${key}`);
        if (raw) {
          const storedEntry = JSON.parse(raw);
          if (storedEntry && storedEntry.expiresAt > now) {
            // Rehydrate in memory for fast subsequent access
            this.memoryCache.set(key, storedEntry);
            return storedEntry.data;
          }
          // Expired in sessionStorage
          window.sessionStorage.removeItem(`${this.storagePrefix}${key}`);
        }
      }
    } catch {
      // JSON parse error or access denied
    }

    return null;
  }

  /**
   * Invalidate a single key
   * @param {string} key
   */
  invalidate(key) {
    if (!key) return;
    this.memoryCache.delete(key);
    try {
      if (typeof window !== "undefined" && window.sessionStorage) {
        window.sessionStorage.removeItem(`${this.storagePrefix}${key}`);
      }
    } catch {
      // Ignore
    }
  }

  /**
   * Invalidate all keys matching a prefix or predicate
   * @param {string} prefix
   */
  invalidatePrefix(prefix) {
    if (!prefix) return;

    // Memory
    for (const key of this.memoryCache.keys()) {
      if (key.startsWith(prefix)) {
        this.memoryCache.delete(key);
      }
    }

    // SessionStorage
    try {
      if (typeof window !== "undefined" && window.sessionStorage) {
        const fullPrefix = `${this.storagePrefix}${prefix}`;
        const keysToRemove = [];
        for (let i = 0; i < window.sessionStorage.length; i++) {
          const k = window.sessionStorage.key(i);
          if (k && k.startsWith(fullPrefix)) {
            keysToRemove.push(k);
          }
        }
        keysToRemove.forEach((k) => window.sessionStorage.removeItem(k));
      }
    } catch {
      // Ignore
    }
  }

  /**
   * Invalidate all ReviewMate cached items (e.g. on logout)
   */
  invalidateAll() {
    this.memoryCache.clear();
    try {
      if (typeof window !== "undefined" && window.sessionStorage) {
        const keysToRemove = [];
        for (let i = 0; i < window.sessionStorage.length; i++) {
          const k = window.sessionStorage.key(i);
          if (k && k.startsWith(this.storagePrefix)) {
            keysToRemove.push(k);
          }
        }
        keysToRemove.forEach((k) => window.sessionStorage.removeItem(k));
      }
    } catch {
      // Ignore
    }
  }
}

export const queryCache = new QueryCache();
export default queryCache;
